/**
 * AppInitializer - Consolidated startup logic service
 *
 * This service handles the entire application initialization sequence in one place,
 * eliminating the complex, fragmented logic spread across App.tsx, GameScreen.tsx,
 * and GameFlowManager.
 *
 * The service follows a simple pattern:
 * 1. Analyze the current state (auth, data, entry point)
 * 2. Execute necessary initialization steps in a predictable order
 * 3. Return a simple state object that tells the UI what to render
 *
 * This replaces the complex web of useEffect dependencies with a single,
 * authoritative initialization process.
 */

import { Platform, Linking } from "react-native";
import { useGameStore } from "../stores/useGameStoreFacade";
import { initializeWebOptimizations } from "../utils/webOptimizations";
import { API_BASE_URL } from "../config/api";
import { TELEMETRY_ENABLED } from "../config/telemetry";
import { gameFlowManager } from "./GameFlowManager";
import { IdentityService, clearStoredPlayerId } from "./IdentityService";
import { TelemetryService } from "./TelemetryService";
import { dailyChallengesService } from "./DailyChallengesService";
import type { FlowDecision } from "./GameFlowManager";

// Removed console.log for performance

export type AppInitializationState =
  | { status: "loading" }
  | {
      status: "legal-page";
      page: "terms" | "privacy" | "dmca" | "about" | "contact";
    }
  | { status: "ready"; flowDecision: FlowDecision }
  | { status: "error"; error: string };

export class AppInitializer {
  private static instance: AppInitializer;
  public identityService: IdentityService;
  public telemetryService: TelemetryService | null = null;

  private constructor() {
    this.identityService = new IdentityService(API_BASE_URL);
  }

  public static getInstance(): AppInitializer {
    if (!AppInitializer.instance) {
      AppInitializer.instance = new AppInitializer();
    }
    return AppInitializer.instance;
  }

  /**
   * Main initialization method - handles the complete app startup sequence
   */
  public async initialize(): Promise<AppInitializationState> {
    console.log("🚀 AppInitializer: initialize() called in local-only mode");

    try {
      // No auth loading needed in local-only mode

      console.log("🚀 AppInitializer: Step 1 - Platform optimizations");
      // Step 1: Initialize platform-specific optimizations
      if (Platform.OS === "web") {
        initializeWebOptimizations();
        console.log("🚀 AppInitializer: Web optimizations initialized");
      }

      console.log("🚀 AppInitializer: Step 2 - Get entry URL");
      // Step 2: Get entry URL and check for special pages
      const entryUrl = await this.getEntryUrl();
      console.log("🚀 AppInitializer: Entry URL:", entryUrl);

      const legalPageCheck = this.checkForLegalPages(entryUrl);
      if (legalPageCheck) {
        console.log(
          "🚀 AppInitializer: Legal page detected, returning:",
          legalPageCheck,
        );
        return legalPageCheck;
      }

      // Step 3: Telemetry (off until there is a consent UI; see
      // config/telemetry.ts). The service starts disabled even when created.
      if (TELEMETRY_ENABLED) {
        const playerId = await this.identityService.getOrCreatePlayerId();
        this.telemetryService = new TelemetryService(API_BASE_URL, playerId);
      } else {
        // Drop any ID stored by earlier builds. Best effort, not awaited.
        clearStoredPlayerId().catch((error) =>
          console.error(
            "AppInitializer: stale player id removal failed",
            error,
          ),
        );
      }

      console.log("🚀 AppInitializer: Step 4 - Load initial data");
      // Step 4: Load initial data (required for all flows)
      await this.loadInitialData();
      console.log("🚀 AppInitializer: Initial data loaded successfully");

      console.log("🚀 AppInitializer: Step 5 - Determine app flow");
      // Step 5: Parse entry point and determine flow
      const flowDecision = await this.determineAppFlow(entryUrl);
      console.log("🚀 AppInitializer: Flow decision:", flowDecision);

      // Step 6: Generate today's daily only. The rest of the month is warmed
      // after first render (scheduleBackgroundWarm, called by App.tsx).
      this.warmToday();

      console.log("🚀 AppInitializer: Initialization completed successfully");
      return { status: "ready", flowDecision };
    } catch (error) {
      console.error("❌ AppInitializer: Initialization failed:", error);
      console.error(
        "❌ AppInitializer: Error stack:",
        error instanceof Error ? error.stack : "No stack",
      );
      return {
        status: "error",
        error:
          error instanceof Error
            ? error.message
            : "Unknown initialization error",
      };
    }
  }

  /**
   * Handle deep links for apps that are already open
   */
  public async handleDeepLink(url: string): Promise<AppInitializationState> {
    try {
      console.log("🔗 AppInitializer: Handling deep link:", url);

      // Handle regular deep links (challenges, etc.)
      const flowDecision = await this.determineAppFlow(url);
      await gameFlowManager.executeFlowDecision(flowDecision);

      console.log("🔗 AppInitializer: Deep link handling complete");
      return { status: "ready", flowDecision };
    } catch (error) {
      console.error("❌ AppInitializer: Deep link handling failed:", error);
      return {
        status: "error",
        error:
          error instanceof Error ? error.message : "Deep link handling failed",
      };
    }
  }

  // Private helper methods

  private async getEntryUrl(): Promise<string> {
    console.log("src/services/AppInitializer.ts: getEntryUrl called");
    if (Platform.OS === "web") {
      return typeof window !== "undefined" ? window.location.href : "";
    } else {
      const initialUrl = await Linking.getInitialURL();
      return initialUrl || "";
    }
  }

  private checkForLegalPages(_entryUrl: string): AppInitializationState | null {
    console.log("src/services/AppInitializer.ts: checkForLegalPages called");
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return null;
    }

    const pathname = window.location.pathname;
    const legalPages = [
      "terms",
      "privacy",
      "dmca",
      "about",
      "contact",
    ] as const;

    for (const page of legalPages) {
      if (pathname === `/${page}`) {
        return { status: "legal-page", page };
      }
    }

    return null;
  }

  private async loadInitialData(): Promise<void> {
    console.log("🚀 AppInitializer: loadInitialData called");

    try {
      console.log("🚀 AppInitializer: Getting gameStore state...");
      const gameStore = useGameStore.getState();
      console.log(
        "🚀 AppInitializer: Got gameStore, calling loadInitialData...",
      );

      const success = await gameStore.loadInitialData();
      console.log("🚀 AppInitializer: loadInitialData returned:", success);

      if (!success) {
        console.warn(
          "🚀 AppInitializer: loadInitialData returned false - treating as non-critical",
        );
        console.warn(
          "🚀 AppInitializer: This can happen during auth state transitions and is usually recoverable",
        );
        // Don't throw an error - let the app continue with default state
        return;
      }

      console.log("🚀 AppInitializer: loadInitialData completed successfully");
    } catch (error) {
      console.error("🚀 AppInitializer: Error in loadInitialData:", error);
      console.error(
        "🚀 AppInitializer: Error stack:",
        error instanceof Error ? error.stack : "No stack",
      );

      // Don't re-throw the error - this prevents app crashes during auth transitions
      console.warn(
        "🚀 AppInitializer: Continuing despite loadInitialData error - app may need manual refresh",
      );
    }
  }

  private async determineAppFlow(entryUrl: string): Promise<FlowDecision> {
    console.log("src/services/AppInitializer.ts: determineAppFlow called");
    const parsedEntry = gameFlowManager.parseEntryUrl(entryUrl);

    console.log("🎮 AppInitializer: Parsed entry data:", parsedEntry);

    // Convert parsed data to the format expected by determineGameFlow
    let entryType: "landing" | "playerChallenge" | "dailyChallenge" = "landing";
    let challengeData: Record<string, unknown> | undefined;

    if (parsedEntry && parsedEntry.isValid) {
      if (parsedEntry.type === "challenge") {
        entryType = "playerChallenge";
        challengeData = {
          startWord: parsedEntry.startWord,
          targetWord: parsedEntry.targetWord,
          isValid: true,
        };
      } else if (parsedEntry.type === "dailychallenge") {
        entryType = "dailyChallenge";
        challengeData = {
          startWord: parsedEntry.startWord,
          targetWord: parsedEntry.targetWord,
          challengeId: parsedEntry.challengeId,
          isValid: true,
        };
      }
      console.log("🎮 AppInitializer: Converted to flow format:", {
        entryType,
        challengeData,
      });
    } else {
      console.log(
        "🎮 AppInitializer: No valid challenge data found, using landing flow",
      );
    }

    return await gameFlowManager.determineGameFlow(entryType, challengeData);
  }

  private warmToday(): void {
    try {
      dailyChallengesService.getTodaysChallenge();
    } catch (error) {
      console.error("AppInitializer: today's daily failed to generate", error);
    }
  }

  /** Warm the daily-challenge cache for the current month. */
  public async warmStartup(): Promise<void> {
    await dailyChallengesService.warmCurrentWindow();
  }

  private backgroundWarmScheduled = false;

  /** Warm the rest of the month off the startup path, once, after a short
   *  delay so first render and first input come first. One date is generated
   *  per idle callback (web, when available) or per timeout tick, so input is
   *  never blocked. Cache-first: opening the calendar sooner just generates
   *  the missing dates on demand. */
  public scheduleBackgroundWarm(delayMs = 1000): void {
    if (this.backgroundWarmScheduled) return;
    this.backgroundWarmScheduled = true;

    const dates = dailyChallengesService.getCurrentWindowDates();
    const ric =
      Platform.OS === "web" &&
      typeof (globalThis as { requestIdleCallback?: unknown })
        .requestIdleCallback === "function"
        ? (
            globalThis as unknown as {
              requestIdleCallback: (
                cb: () => void,
                opts?: { timeout: number },
              ) => number;
            }
          ).requestIdleCallback
        : null;

    let next = 0;
    const step = (): void => {
      dailyChallengesService.warmDate(dates[next++]);
      if (next < dates.length) schedule(0);
    };
    const schedule = (delay: number): void => {
      if (ric) ric(step, { timeout: delay === 0 ? 2000 : delayMs * 2 });
      else setTimeout(step, delay);
    };
    schedule(delayMs);
  }
}

// AppInitializer instance created
export const appInitializer = AppInitializer.getInstance();
